import { Injectable } from '@angular/core';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import {
  isCommercialList,
  isEpicShow,
  isNormalList,
  isPowerList,
} from '@epicuro-next/features/engagement/list-utils';
import { PRODUCT_LIST_SUBROUTES } from '@epicuro-next/navigation/constants';
import {
  selectIsOpenedListDeleted,
  selectIsOwnerOfListEntity,
  selectOpenedListFromListEntity,
} from '@epicuro-next/state/list-entities';
import {
  selectIsCurrentUserAllowedToEdit,
  selectShopTheListLoading,
  State as ProductListState,
} from '@epicuro-next/state/product-list-state';
import { selectUrl } from '@epicuro-next/state/router-store';
import { selectIsSalesRepOrAdmin } from '@epicuro-next/state/user-state';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

@Injectable()
export class ListAccessProviderService implements AbstractDataProvider {
  private bindings = new Map<string, Observable<unknown>>();
  private destroy$ = new Subject<void>();

  private readonly shopListLoading$ = this.store.pipe(
    select(selectShopTheListLoading),
  );

  private list$ = this.store.pipe(
    select(selectOpenedListFromListEntity),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public isListDetailTab$ = this.store.pipe(
    select(selectUrl),
    map((url) => url.includes(PRODUCT_LIST_SUBROUTES.details)),
  );

  public isOwnerOfOpenedList$ = this.store.pipe(
    select(selectIsOwnerOfListEntity()),
  );

  public isListAllowedForEdit$ = this.store.pipe(
    select(selectIsCurrentUserAllowedToEdit),
  );

  public isListDeleted$ = this.store.pipe(select(selectIsOpenedListDeleted));

  public isListCommercial$ = this.list$.pipe(
    map((list) => {
      if (!list) {
        return false;
      }

      return isCommercialList(list);
    }),
  );

  public isListNormal$ = this.list$.pipe(
    map((list) => {
      if (!list) {
        return true;
      }

      return isNormalList(list);
    }),
  );

  public isPowerList$ = this.list$.pipe(
    map((list) => {
      if (!list) {
        return false;
      }

      return isPowerList(list);
    }),
  );

  public isEpicShow$ = this.list$.pipe(
    map((list) => {
      if (!list) {
        return false;
      }

      return isEpicShow(list);
    }),
  );

  public public = this.list$.pipe(
    map((_) => {
      return false;
    }),
  );

  public readonly shopTheListDisabled$ = combineLatest([
    this.list$,
    this.store.pipe(select(selectIsSalesRepOrAdmin)),
    this.isOwnerOfOpenedList$,
    this.shopListLoading$,
  ]).pipe(
    map(([list, isSalesRepOrAdmin, isOwner, loading]) => {
      if (!list || loading) return true;

      const hasProductsToBuy = (list.availableProductsCount ?? 0) > 0;
      if (!hasProductsToBuy) return true;

      if (isSalesRepOrAdmin) return false;

      const userConsumingEpicShow =
        !isSalesRepOrAdmin && !isOwner && isEpicShow(list);
      if (userConsumingEpicShow) return true;

      return false;
    }),
  );

  constructor(private store: Store<ProductListState>) {
    this.initBindings();
  }

  public read<T>(property: string): Observable<T> {
    const observable = this.bindings.get(property);

    if (!observable) {
      throw new Error(
        'Undefined property of ListAccessProviderService:' + property,
      );
    }

    return observable as Observable<T>;
  }

  public write() {
    // here for eslint
  }

  private initBindings() {
    const bindingsEntries: [string, Observable<unknown>][] = [
      ['isListAllowedForEdit', this.isListAllowedForEdit$],
      ['isListCommercial', this.isListCommercial$],
      ['isOwnerOfOpenedList', this.isOwnerOfOpenedList$],
      ['isEpicShow', this.isEpicShow$],
      ['shopTheListDisabled', this.shopTheListDisabled$],
      ['isPowerList', this.isPowerList$],
      ['isListNormal', this.isListNormal$],
      ['isListDetailTab', this.isListDetailTab$],
      ['isListDeleted', this.isListDeleted$],
    ];

    bindingsEntries.forEach(([propertyName, observer]) => {
      this.initBinding(propertyName, observer);
    });
  }

  private initBinding(propertyName: string, observer: Observable<unknown>) {
    this.bindings.set(
      propertyName,
      observer.pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntil(this.destroy$),
      ),
    );
  }
}
